"use client";
import { BuilderBlockProps } from "@/types";
import { FC, useEffect, useRef } from "react";

type ContentReplicatorBlockProps = {
  classSelectors?: string;
} & BuilderBlockProps;

export const ContentReplicatorBlock: FC<ContentReplicatorBlockProps> = ({
  attributes,
  classSelectors = "",
}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!classSelectors || !containerRef.current) return;

    const container = containerRef.current;
    const selectors = classSelectors
      .split(",")
      .map((s) => s.trim())
      .filter((s) => s.length > 0);

    let hasCloned = false;
    let observer: MutationObserver | null = null;
    let timeoutId: number | null = null;

    const cleanup = () => {
      observer?.disconnect();
      observer = null;
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
        timeoutId = null;
      }
    };

    const tryClone = () => {
      if (hasCloned) return;

      const allSourcesExist = selectors.every((selector) => {
        try {
          const sources = document.querySelectorAll(selector);
          return sources.length > 0;
        } catch (error) {
          console.error(`Invalid selector: "${selector}"`, error);
          return false;
        }
      });

      if (allSourcesExist) {
        container.innerHTML = "";

        selectors.forEach((selector) => {
          try {
            const sources = document.querySelectorAll(selector);

            sources.forEach((source) => {
              // Deep clone the element and all its children
              const clonedContent = source.cloneNode(true) as HTMLElement;
              container.appendChild(clonedContent);
            });
          } catch (error) {
            console.error(`Invalid selector: "${selector}"`, error);
          }
        });

        hasCloned = true;
        cleanup();
      }
    };

    tryClone();

    // If sources don't exist yet, watch for DOM changes
    //
    if (!hasCloned) {
      observer = new MutationObserver(tryClone);
      observer.observe(document.body, { childList: true, subtree: true });

      timeoutId = window.setTimeout(() => {
        cleanup();
      }, 15000);
    }

    return () => cleanup();
  }, [classSelectors]);

  return <div {...attributes} ref={containerRef} />;
};
